import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import LogShopTimeDialog from '../components/LogShopTimeDialog';
import { Card } from '../components/ui/card';
import { Briefcase, Users, Wrench, FileBarChart, AlertTriangle, Clock, MoonStar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  intake: 'Intake',
  teardown: 'Teardown',
  quote: 'Quote',
  waiting_parts: 'Waiting for Parts',
  waiting_approval: 'Waiting for Approval',
  subcontractor: 'At Subcontractor',
  machining: 'Machining',
  reassembly: 'Reassembly',
  completed: 'Completed'
};

const statusColors = {
  intake: 'bg-blue-500',
  teardown: 'bg-purple-500',
  quote: 'bg-yellow-500',
  waiting_parts: 'bg-orange-500',
  waiting_approval: 'bg-amber-500',
  subcontractor: 'bg-pink-500',
  machining: 'bg-indigo-500',
  reassembly: 'bg-teal-500',
  completed: 'bg-emerald-500'
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchCompliance();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reports/dashboard`);
      setStats(response.data);
    } catch {
      // Failed to fetch stats
    }
  };

  const fetchCompliance = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/compliance-summary`);
      setComplianceSummary(response.data);
    } catch {
      // Failed
    }
  };

  const effectivePerms = user?.effective_permissions || [];
  const canView = (perm) => effectivePerms.includes(perm) || !effectivePerms.length; // fallback: show if not loaded
  const allStatCards = [
    { label: 'Total Jobs', value: stats?.total_jobs || 0, icon: Briefcase, color: 'bg-blue-500', path: '/jobs', perm: 'jobs' },
    { label: 'Technicians', value: stats?.total_technicians || 0, icon: Users, color: 'bg-purple-500', path: '/technicians', perm: 'technicians' },
    { label: 'Equipment', value: stats?.total_equipment || 0, icon: Wrench, color: 'bg-orange-500', path: '/equipment', perm: 'equipment' },
    { label: 'Reports', value: 'View', icon: FileBarChart, color: 'bg-green-500', path: '/reports', perm: 'reports' },
  ];
  const statCards = allStatCards.filter((c) => canView(c.perm));

  return (
    <DashboardLayout>
      <div data-testid="dashboard-page" className="space-y-8">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Welcome, {user?.name}</h3>
            <p className="text-slate-600">Here's what's happening in your shop today</p>
          </div>
          <LogShopTimeDialog />
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.label}
                className="p-6 bg-white border border-slate-200 shadow-sm hover:border-slate-400 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(stat.path)}
                data-testid={`stat-card-${stat.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">{stat.label}</p>
                    <p className="text-3xl font-black text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-md`}>
                    <Icon size={24} className="text-white" />
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Equipment Compliance Card */}
        {complianceSummary && (complianceSummary.overdue > 0 || complianceSummary.upcoming > 0 || complianceSummary.snoozed > 0) && (
          <Card
            className={`p-6 bg-white border border-slate-200 shadow-sm transition-all ${user?.role !== 'technician' ? 'hover:shadow-md cursor-pointer' : ''}`}
            onClick={user?.role !== 'technician' ? () => navigate('/reports?tab=compliance') : undefined}
            data-testid="compliance-card"
          >
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-slate-900">Equipment Compliance</h4>
              {user?.role !== 'technician' && <span className="text-xs text-slate-500">Click to view full report →</span>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className={`p-4 rounded-md border ${complianceSummary.overdue > 0 ? 'bg-red-50 border-red-200 text-red-800' : 'bg-slate-50 border-slate-200 text-slate-500'}`} data-testid="dash-compliance-overdue">
                <div className="flex items-center gap-2 text-xs uppercase font-bold"><AlertTriangle size={14} /> Overdue</div>
                <div className="text-3xl font-black mt-1 font-mono">{complianceSummary.overdue}</div>
              </div>
              <div className={`p-4 rounded-md border ${complianceSummary.upcoming > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-slate-50 border-slate-200 text-slate-500'}`} data-testid="dash-compliance-upcoming">
                <div className="flex items-center gap-2 text-xs uppercase font-bold"><Clock size={14} /> Upcoming ({complianceSummary.upcoming_days}d)</div>
                <div className="text-3xl font-black mt-1 font-mono">{complianceSummary.upcoming}</div>
              </div>
              <div className="p-4 rounded-md border bg-slate-50 border-slate-200 text-slate-600" data-testid="dash-compliance-snoozed">
                <div className="flex items-center gap-2 text-xs uppercase font-bold"><MoonStar size={14} /> Snoozed</div>
                <div className="text-3xl font-black mt-1 font-mono">{complianceSummary.snoozed}</div>
              </div>
            </div>
          </Card>
        )}

        {/* Jobs by Status */}
        {stats?.jobs_by_status && Object.keys(stats.jobs_by_status).length > 0 && (
          <Card className="p-6 bg-white border border-slate-200 shadow-sm">
            <h4 className="text-lg font-bold text-slate-900 mb-4">Jobs by Status</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(stats.jobs_by_status).map(([status, count]) => (
                <div
                  key={status}
                  className="p-4 bg-slate-50 rounded-md border border-slate-200 hover:border-slate-400 hover:shadow-sm transition-all cursor-pointer group"
                  onClick={() => navigate(`/jobs?status=${status}`)}
                  data-testid={`status-tile-${status}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`w-2.5 h-2.5 rounded-full ${statusColors[status] || 'bg-slate-400'}`} />
                    <p className="text-xs uppercase tracking-wider font-bold text-slate-500 group-hover:text-slate-700 transition-colors">
                      {statusLabels[status] || status.replace('_', ' ')}
                    </p>
                  </div>
                  <p className="text-2xl font-black text-slate-900">{count}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
